/**
 * Scoped interaction controller for compact header dropdowns (Ready Designs
 * today; generic to any future top-level menu item that has a submenu).
 *
 * Desktop interaction is click/keyboard only — there is no hover-to-open.
 * Hover-intent timers were tried and removed: real pointer movement during
 * the dropdown's open transition could momentarily hit-test outside the
 * trigger's subtree (see prior investigation), and stacking more pointer
 * logic on top of that made the interaction harder to reason about, not more
 * reliable. Click/keyboard is the single source of truth for open state.
 *
 * The native header-menu component's own activate()/deactivate() methods are
 * still called (they carry useful side effects — submenu height measurement,
 * the --submenu-height/--full-open-header-height CSS variables the rest of
 * the header's CSS depends on) but this controller is the only thing that
 * decides when to call them; the native pointerenter/pointerleave/focus/blur
 * bindings that used to call them directly are removed for these items in
 * blocks/_header-menu.liquid.
 */
(function () {
  /** @param {Element} trigger */
  function isOpen(trigger) {
    return trigger.getAttribute('aria-expanded') === 'true';
  }

  /** @param {EventTarget | null} target */
  function findTrigger(target) {
    if (!(target instanceof Element)) return null;
    const trigger = target.closest('a[ref="menuitem"][aria-haspopup]');
    if (!trigger?.closest('header-menu')) return null;
    if (trigger.closest('.menu-list__list-item[slot="overflow"]')) return null;
    return trigger;
  }

  /** @param {Element} trigger */
  function getController(trigger) {
    const li = trigger.closest('.menu-list__list-item:not([slot="overflow"])');
    const submenu = li?.querySelector(':scope > [ref="submenu[]"]');
    const headerMenu = li?.closest('header-menu');
    if (!li || !submenu || !headerMenu) return null;
    return { li, headerMenu };
  }

  /** @param {Element} trigger */
  function open(trigger) {
    const controller = getController(trigger);
    if (!controller || isOpen(trigger)) return;
    controller.headerMenu.activate({ target: controller.li });
  }

  /** @param {Element} trigger */
  function close(trigger) {
    const controller = getController(trigger);
    if (!controller || !isOpen(trigger)) return;
    controller.headerMenu.deactivate({ target: controller.li });
  }

  /** @param {Element} trigger */
  function toggle(trigger) {
    if (isOpen(trigger)) {
      close(trigger);
    } else {
      open(trigger);
    }
  }

  document.addEventListener('click', (event) => {
    const trigger = findTrigger(event.target);
    if (trigger) {
      // The trigger only ever toggles - it never navigates.
      event.preventDefault();
      toggle(trigger);
      return;
    }

    document.querySelectorAll('header-menu a[ref="menuitem"][aria-haspopup][aria-expanded="true"]').forEach((openTrigger) => {
      const controller = getController(openTrigger);
      if (!controller) return;
      if (event.target instanceof Node && controller.li.contains(event.target)) return;
      close(openTrigger);
    });
  });

  document.addEventListener('keydown', (event) => {
    const trigger = findTrigger(event.target);
    if (trigger && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      toggle(trigger);
      return;
    }

    if (event.key !== 'Escape' || !(event.target instanceof Element)) return;
    const li = event.target.closest('header-menu .menu-list__list-item:not([slot="overflow"])');
    const itemTrigger = li?.querySelector(':scope > a[ref="menuitem"][aria-haspopup]');
    if (!itemTrigger || !isOpen(itemTrigger)) return;
    close(itemTrigger);
    itemTrigger.focus();
  });

  document.addEventListener('focusout', (event) => {
    if (!(event.target instanceof Element)) return;
    const li = event.target.closest('header-menu .menu-list__list-item:not([slot="overflow"])');
    const trigger = li?.querySelector(':scope > a[ref="menuitem"][aria-haspopup]');
    if (!li || !trigger) return;

    // Defer so document.activeElement reflects the new focus target across browsers.
    setTimeout(() => {
      if (!li.contains(document.activeElement)) {
        close(trigger);
      }
    }, 0);
  });
})();
